// Segments (PRD 137 Wave 1): the api's shapes for segment dimensions, their
// values, and the tags a journal entry carries. In their own file rather than
// the businessApi hub, which nearly every test imports.

import {
  BulkCategorizeParameters,
  CreateJournalEntryParams,
  EmailReportRequest,
  ReportKind,
  ReportPropertyFilterEcho,
  ReportScheduleParams,
} from './businessApi';

/** A business has exactly three slots; a dimension never changes slot. */
export type SegmentSlot = 1 | 2 | 3;
export const SEGMENT_SLOTS: readonly SegmentSlot[] = [1, 2, 3];

/** Slot numbers as the JSON keys of a `segments` hash. */
export type SegmentSlotKey = '1' | '2' | '3';

/** What a slot means, for imports and defaults; the screens say `name`. */
export type SegmentDimensionKey = 'class' | 'department' | 'location' | 'custom';

/** `warn` flags an untagged P&L line and saves anyway (§4.7, Wave 4). */
export type SegmentRequired = 'off' | 'warn';

/**
 * `Segments::Access` for the business (§4.11): `read_only` after the module
 * lapses (tags stay, nothing new), `off` when it was never held and enforced.
 */
export type SegmentsAccess = 'writable' | 'read_only' | 'off';

export interface SegmentDimension {
  /** null until the slot is first saved. */
  id: string | null;
  slot: SegmentSlot;
  key: SegmentDimensionKey;
  name: string;
  active: boolean;
  required: SegmentRequired;
  /** False for a slot the business never touched (default name, off). */
  saved: boolean;
  value_count: number;
}

export interface SegmentDimensionParams {
  name?: string;
  key?: SegmentDimensionKey;
  active?: boolean;
  required?: SegmentRequired;
}

export interface SegmentDimensionsResult {
  dimensions: SegmentDimension[];
  access: SegmentsAccess;
}

export interface SegmentValue {
  id: string;
  slot: SegmentSlot;
  segment_dimension_id: string;
  name: string;
  /** "West › Denver" when nested; the name otherwise. */
  display_name: string;
  code: string | null;
  parent_id: string | null;
  active: boolean;
}

export interface CreateSegmentValueParams {
  slot: SegmentSlot;
  name: string;
  code?: string;
  parent_id?: string;
}

export interface UpdateSegmentValueParams {
  name?: string;
  code?: string | null;
  parent_id?: string | null;
  active?: boolean;
}

/** Slot → value id. Absent means unchanged; null clears the slot. */
export type SegmentTags = Partial<Record<SegmentSlotKey, string | null>>;

/** The JE create payload with the tags Wave 1 adds to the entry and lines. */
export type CreateJournalEntryWithSegments = Omit<CreateJournalEntryParams, 'lines'> & {
  segments?: SegmentTags;
  lines: (CreateJournalEntryParams['lines'][number] & { segments?: SegmentTags })[];
};

/** The error codes the segment endpoints answer with. */
export const SEGMENT_ERROR_CODES = {
  /** Enforced module, not held: tagging and settings are refused. */
  moduleRequired: 'module_required',
  /** A value was added to a slot that is not turned on yet. */
  notSetUp: 'segment_not_set_up',
  /** Delete refused: posted lines carry the value, or it has children. */
  inUse: 'segment_value_in_use',
  /** Not an owner or accountant. */
  forbidden: 'forbidden',
} as const;

// ---- Wave 2: documents, bank categorisation, bulk categorise, rules ----

/** Slot → the value's name, as a document line's response carries it. */
export type SegmentNames = Partial<Record<SegmentSlotKey, string | null>>;

/**
 * What an invoice, bill, credit memo, vendor credit or recurring template
 * line carries once the business has any segment dimension (absent before).
 */
export interface SegmentTaggedLineWire {
  segments?: SegmentTags;
  segment_names?: SegmentNames;
}

/** The header half of the same: present only alongside the lines'. */
export interface SegmentTaggedDocumentWire {
  segments?: SegmentTags;
}

/**
 * Form state for one set of tags: slot → value id. A slot that is absent was
 * never touched (and is never sent as a change); '' means a person cleared it.
 */
export type SegmentSlotTags = Partial<Record<SegmentSlotKey, string>>;

/** Bulk categorise: one set of tags for every transaction, beside its one property_id. */
export type BulkCategorizeWithSegments = BulkCategorizeParameters & { segments?: SegmentTags };

// ---- Wave 3: reports ----

/**
 * One segment filter on a report (PRD 137 §4.5), in PropertyFilter's modes:
 * no filter is `null`, never an empty selection. A parent value includes its
 * children server-side. Ids and "unassigned only" never combine — the api
 * refuses both at once.
 */
export type ReportSegmentSelection =
  | { slot: SegmentSlot; mode: 'ids'; ids: string[] }
  | { slot: SegmentSlot; mode: 'unassigned' };

/** Mixed into a report query: absent or null sends no segment params at all. */
export interface SegmentFilterQuery {
  segment?: ReportSegmentSelection | null;
}

/** What a filtered report echoes back; absent on an unfiltered one. */
export interface SegmentFilterEcho {
  slot: SegmentSlot;
  dimension: string;
  value_ids: string[];
  unassigned: boolean;
}

/** Any report response that may carry the echo. */
export interface SegmentFilteredReportWire {
  segment_filter?: SegmentFilterEcho;
}

/** The stored form a report schedule keeps (`report_schedules.segment_filter`). */
export interface SegmentFilterWire {
  slot: SegmentSlot;
  value_ids: string[];
  unassigned: boolean;
}

/** The segment params as the report endpoints read them (query or POST body). */
export interface SegmentFilterParams {
  segment_slot?: SegmentSlot;
  segment_value_ids?: string[];
  segment_unassigned?: boolean;
}

/** The error codes a segment filter can be refused with. */
export const SEGMENT_FILTER_ERROR_CODES = {
  /** A malformed filter: no slot, an unknown value, ids and unassigned at once. */
  invalid: 'invalid_segment_filter',
  /** The slot names a dimension this business never set up. */
  notSetUp: 'segment_not_set_up',
} as const;

/** A GL register row once the business has any segment dimension. */
export interface SegmentRegisterRowWire {
  /** Slot → value id; null on a grouped sales-receipt row. */
  segments?: SegmentTags | null;
  /** Slot → "West › Denver"; null on a grouped sales-receipt row. */
  segment_names?: SegmentNames | null;
}

// ---- P&L by segment (GET reports/profit-and-loss/by-segment) ----

/**
 * `value` a leaf, `parent_direct` what was tagged to a parent itself,
 * `parent_subtotal` the parent with its children (not part of Total),
 * then `unassigned` and `total`.
 */
export type PnlBySegmentColumnKind = 'value' | 'parent_direct' | 'parent_subtotal' | 'unassigned' | 'total';

export interface PnlBySegmentColumn {
  /** A leaf's value id, "<id>:parent_direct", "<id>:parent_subtotal", "unassigned", "total". */
  key: string;
  kind: PnlBySegmentColumnKind;
  segment_value_id: string | null;
  name: string;
  /** The column heading: "Denver", "West (direct)", "Total West". */
  label: string;
  parent_id: string | null;
  active: boolean;
}

/** Column key → 4dp money string. */
export type PnlBySegmentAmounts = Record<string, string>;

export interface PnlBySegmentLine {
  gl_account_id: string;
  account_number: string | null;
  name: string;
  subtype: string | null;
  amounts: PnlBySegmentAmounts;
}

export interface PnlBySegmentSection {
  key: string;
  name: string;
  type: 'income' | 'expenses';
  lines: PnlBySegmentLine[];
  subtotals: PnlBySegmentAmounts;
}

export interface PnlBySegmentTotals {
  revenue: PnlBySegmentAmounts;
  cost_of_goods_sold: PnlBySegmentAmounts;
  gross_profit: PnlBySegmentAmounts;
  operating_expenses: PnlBySegmentAmounts;
  operating_income: PnlBySegmentAmounts;
  other_income: PnlBySegmentAmounts;
  other_expenses: PnlBySegmentAmounts;
  net_income: PnlBySegmentAmounts;
}

export interface ProfitAndLossBySegmentReport {
  report: 'profit_and_loss_by_segment';
  basis: 'accrual';
  business_id: string;
  from: string;
  to: string;
  property_filter: ReportPropertyFilterEcho;
  currency: string;
  dimension: { id: string; slot: SegmentSlot; key: SegmentDimensionKey; name: string; active: boolean };
  columns: PnlBySegmentColumn[];
  sections: PnlBySegmentSection[];
  totals: PnlBySegmentTotals;
}

/** The catalogue key the new report is emailed and scheduled under. */
export const PNL_BY_SEGMENT_REPORT = 'profit_and_loss_by_segment' as const;

/** Every report the catalogue can email or schedule, P&L by segment included. */
export type ReportKindWithSegments = ReportKind | typeof PNL_BY_SEGMENT_REPORT;

/**
 * POST reports/email with segments: a filter on the P&L family, or the
 * dimension (`segment_slot` alone) for P&L by segment.
 */
export type EmailReportWithSegments = Omit<EmailReportRequest, 'report'> &
  SegmentFilterQuery & {
    report: ReportKindWithSegments;
    segment_slot?: SegmentSlot;
  };

/** A schedule as the api returns it once Wave 3 is on: `segment_filter` null for none. */
export interface ReportScheduleSegmentWire {
  segment_filter?: SegmentFilterWire | null;
}

/** Create/update: null clears, absent keeps. */
export type ReportScheduleParamsWithSegments = Omit<ReportScheduleParams, 'report_kind'> & {
  report_kind: ReportKindWithSegments;
  segment_filter?: SegmentFilterWire | null;
};
